
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type FxaaShaderUniforms = {
	uInputTexture: WebGLUniformLocation,
	uInverseResolution: WebGLUniformLocation
};

export type FxaaShaderAttributes = {
	aVertexPosition: number,
	vTextureCoord: number
};

export type FxaaShaderSettings = string[];

export class FxaaShader extends ShaderBase<FxaaShaderUniforms, FxaaShaderAttributes> {

    private static fragmentShaderSource = `
// FXAA 3.11 — Fast Approximate Anti-Aliasing
// Based on the algorithm by Timothy Lottes (NVIDIA), adapted for WebGL2/GLSL ES 3.0.

out vec4 fragColor;

uniform sampler2D uInputTexture;
// 1.0 / viewport size
uniform vec2 uInverseResolution;

in vec2 vTextureCoord;

// Luminance approximation from RGB (perceptual weights).
float luminance(vec3 color) {
    return dot(color, vec3(0.299, 0.587, 0.114));
}

void main() {
    vec2 uv = vTextureCoord;
    vec2 texel = uInverseResolution;

    // Sample the center and 4 neighbors
    vec3 rgbM  = texture(uInputTexture, uv).rgb;
    vec3 rgbN  = texture(uInputTexture, uv + vec2( 0.0, -texel.y)).rgb;
    vec3 rgbS  = texture(uInputTexture, uv + vec2( 0.0,  texel.y)).rgb;
    vec3 rgbW  = texture(uInputTexture, uv + vec2(-texel.x,  0.0)).rgb;
    vec3 rgbE  = texture(uInputTexture, uv + vec2( texel.x,  0.0)).rgb;

    float lumM = luminance(rgbM);
    float lumN = luminance(rgbN);
    float lumS = luminance(rgbS);
    float lumW = luminance(rgbW);
    float lumE = luminance(rgbE);

    float lumMin = min(lumM, min(min(lumN, lumS), min(lumW, lumE)));
    float lumMax = max(lumM, max(max(lumN, lumS), max(lumW, lumE)));
    float lumRange = lumMax - lumMin;

    // Early exit: if contrast is below threshold, no AA needed.
    // FXAA_EDGE_THRESHOLD: tradeoff between quality and performance.
    // FXAA_EDGE_THRESHOLD_MIN: avoid processing very dark areas.
    const float FXAA_EDGE_THRESHOLD     = 0.125;
    const float FXAA_EDGE_THRESHOLD_MIN = 0.0625;

    if (lumRange < max(FXAA_EDGE_THRESHOLD_MIN, lumMax * FXAA_EDGE_THRESHOLD)) {
        fragColor = vec4(rgbM, 1.0);
        return;
    }

    // Sample the 4 diagonal neighbors
    vec3 rgbNW = texture(uInputTexture, uv + vec2(-texel.x, -texel.y)).rgb;
    vec3 rgbNE = texture(uInputTexture, uv + vec2( texel.x, -texel.y)).rgb;
    vec3 rgbSW = texture(uInputTexture, uv + vec2(-texel.x,  texel.y)).rgb;
    vec3 rgbSE = texture(uInputTexture, uv + vec2( texel.x,  texel.y)).rgb;

    float lumNW = luminance(rgbNW);
    float lumNE = luminance(rgbNE);
    float lumSW = luminance(rgbSW);
    float lumSE = luminance(rgbSE);

    // Compute edge direction
    float lumNS = lumN + lumS;
    float lumWE = lumW + lumE;
    float lumNWSW = lumNW + lumSW;
    float lumNENE = lumNE + lumSE; // NE + SE edge
    float lumNWNE = lumNW + lumNE;
    float lumSWSE = lumSW + lumSE;

    // Determine if the edge is horizontal or vertical
    float edgeHorz = abs(lumNWSW - 2.0 * lumW) +
                     abs(lumNS   - 2.0 * lumM) * 2.0 +
                     abs(lumNENE - 2.0 * lumE);
    float edgeVert = abs(lumNWNE - 2.0 * lumN) +
                     abs(lumWE   - 2.0 * lumM) * 2.0 +
                     abs(lumSWSE - 2.0 * lumS);

    bool isHorizontal = edgeHorz >= edgeVert;

    // Choose the step direction perpendicular to the edge
    float stepLength = isHorizontal ? texel.y : texel.x;
    float gradientPos, gradientNeg;

    if (isHorizontal) {
        gradientPos = lumS - lumM;
        gradientNeg = lumN - lumM;
    } else {
        gradientPos = lumE - lumM;
        gradientNeg = lumW - lumM;
    }

    bool pairIsPositive = abs(gradientPos) >= abs(gradientNeg);
    float gradient = max(abs(gradientPos), abs(gradientNeg));

    if (!pairIsPositive) {
        stepLength = -stepLength;
    }

    // Shift UV to edge center (half a texel perpendicular to edge)
    vec2 edgeUV = uv;
    if (isHorizontal) {
        edgeUV.y += stepLength * 0.5;
    } else {
        edgeUV.x += stepLength * 0.5;
    }

    // Walk along the edge in both directions to find endpoints
    vec2 edgeStep = isHorizontal ? vec2(texel.x, 0.0) : vec2(0.0, texel.y);
    float edgeThreshold = gradient * 0.25;

    // Search positive direction
    vec2 uvPos = edgeUV + edgeStep;
    float lumEndPos = luminance(texture(uInputTexture, uvPos).rgb) - (pairIsPositive ? lumS + lumM : lumN + lumM) * 0.5;
    bool reachedPos = abs(lumEndPos) >= edgeThreshold;

    for (int i = 0; i < 10 && !reachedPos; i++) {
        uvPos += edgeStep;
        lumEndPos = luminance(texture(uInputTexture, uvPos).rgb) - (pairIsPositive ? lumS + lumM : lumN + lumM) * 0.5;
        reachedPos = abs(lumEndPos) >= edgeThreshold;
    }

    // Search negative direction
    vec2 uvNeg = edgeUV - edgeStep;
    float lumEndNeg = luminance(texture(uInputTexture, uvNeg).rgb) - (pairIsPositive ? lumS + lumM : lumN + lumM) * 0.5;
    bool reachedNeg = abs(lumEndNeg) >= edgeThreshold;

    for (int i = 0; i < 10 && !reachedNeg; i++) {
        uvNeg -= edgeStep;
        lumEndNeg = luminance(texture(uInputTexture, uvNeg).rgb) - (pairIsPositive ? lumS + lumM : lumN + lumM) * 0.5;
        reachedNeg = abs(lumEndNeg) >= edgeThreshold;
    }

    // Compute distance to each endpoint
    float distPos, distNeg;
    if (isHorizontal) {
        distPos = uvPos.x - uv.x;
        distNeg = uv.x - uvNeg.x;
    } else {
        distPos = uvPos.y - uv.y;
        distNeg = uv.y - uvNeg.y;
    }

    float distMin = min(distPos, distNeg);
    float edgeLength = distPos + distNeg;
    float pixelOffset = 0.5 - distMin / edgeLength;

    // Only apply offset if the endpoint luminance indicates we're on the correct side of the edge
    bool correctSide = (distPos < distNeg) ? (lumEndPos < 0.0 != lumM < (pairIsPositive ? lumS : lumN)) :
                                              (lumEndNeg < 0.0 != lumM < (pairIsPositive ? lumS : lumN));

    float finalOffset = correctSide ? pixelOffset : 0.0;

    // Subpixel anti-aliasing: also blend based on local contrast to reduce shimmer
    float lumaAvg = (lumN + lumS + lumW + lumE) * 0.25;
    float subpixelOffset = clamp(abs(lumaAvg - lumM) / lumRange, 0.0, 1.0);
    subpixelOffset = smoothstep(0.0, 1.0, subpixelOffset);
    subpixelOffset = subpixelOffset * subpixelOffset * 0.75; // FXAA_SUBPIX_TRIM scaled

    finalOffset = max(finalOffset, subpixelOffset);

    // Apply the offset perpendicular to the edge
    vec2 finalUV = uv;
    if (isHorizontal) {
        finalUV.y += finalOffset * stepLength;
    } else {
        finalUV.x += finalOffset * stepLength;
    }

    fragColor = vec4(texture(uInputTexture, finalUV).rgb, 1.0);
}
`;

    private static vertexShaderSource = `
// xy = vertex position in normalized device coordinates ([-1,+1] range).
layout(location = 0) in vec2 aVertexPosition;

out vec2 vTextureCoord;

const vec2 scale = vec2(0.5, 0.5);

void main()
{
    vTextureCoord  = aVertexPosition * scale + scale; // scale vertex attribute to [0,1] range
    gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: FxaaShaderUniformsContainer;
    public attributesContainer: FxaaShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: FxaaShaderSettings) {
        super(ctx, FxaaShader.fragmentShaderSource, FxaaShader.vertexShaderSource, settings);
        this.uniformsContainer = new FxaaShaderUniformsContainer(ctx, this);
        this.attributesContainer = new FxaaShaderShaderAttributesContainer(ctx, this);
    }
}

class FxaaShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: FxaaShader) {
        super(ctx);
    }

    public setInputTexture(texture: Texture) : FxaaShaderUniformsContainer {
        this.setTextureValue('uInputTexture', this.shader.uniforms.uInputTexture, texture, 'sampler2D');
        return this;
    }

    public setInverseResolution(value: vec2 | number[]) : FxaaShaderUniformsContainer {
        this.setValue('uInverseResolution', this.shader.uniforms.uInverseResolution, value, 'vec2');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uInputTexture', uniform: this.shader.uniforms.uInputTexture, dataType: 'sampler2D' }
        ];
    }
}

class FxaaShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: FxaaShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : FxaaShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTextureCoord(settings: { length: number, offset: number, itemSize: number }) : FxaaShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTextureCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}